import { useEffect, useMemo, useState } from "react";
import HSeparator from "../components/HSeparator";
import TitleBar from "../components/TitleBar/TitleBar";
import EquipmentSlot, {
    SlotOption,
} from "../components/HunterFashion/EquipmentSlot";
import ImageLayoutEven from "../components/HunterFashion/ImageLayouts/ImageLayoutEven";
import ImageLayoutWide4 from "../components/HunterFashion/ImageLayouts/ImageLayoutWide4";
import ImageLayoutWide2 from "../components/HunterFashion/ImageLayouts/ImageLayoutWide2";
import ImageLayoutCarouselLeft from "../components/HunterFashion/ImageLayouts/ImageLayoutCarouselLeft";
import ImageLayoutTrio from "../components/HunterFashion/ImageLayouts/ImageLayoutTrio";
import ImageLayoutCarouselCenter from "../components/HunterFashion/ImageLayouts/ImageLayoutCarouselCenter";

import {
    HunterOutfit,
    MonsterHunterGame,
    MonsterHunterSlot,
} from "@mythic/coms/types";

import styles from "./HunterFashion.module.css";
import { useNavigate, useParams } from "react-router-dom";
import { getContrastColor } from "../components/SelectStyle";
import TagList from "../components/HunterFashion/TagList";
import { useUser } from "../contexts/UserContext";
import { useModal } from "../contexts/ModalContext";
import ConfirmationModal from "../components/CharacterCreator/ConfirmationModal";

export default function HunterFashionUploadPage() {
    const navigate = useNavigate();
    const { showModal, hideModal } = useModal();

    const { outfitId } = useParams();
    const { user } = useUser();

    const [armorDb, setArmorDb] = useState<
        (SlotOption & {
            slotType: MonsterHunterSlot;
            game: MonsterHunterGame;
            dyeable: boolean;
        })[]
    >([]);

    const [weaponDb, setWeaponDb] = useState<
        (SlotOption & { game: MonsterHunterGame })[]
    >([]);

    const [outfitData, setOutfitData] = useState<HunterOutfit | null>(null);

    const headOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "head"),
        [armorDb],
    );

    const chestOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "chest"),
        [armorDb],
    );

    const armsOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "arms"),
        [armorDb],
    );

    const waistOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "waist"),
        [armorDb],
    );

    const legsOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "legs"),
        [armorDb],
    );

    useEffect(() => {
        if (!outfitId) return;

        fetch(
            `${import.meta.env.VITE_SERVER_URL}/api/hunterfashion/${outfitId}/get`,
        )
            .then((response) => response.json())
            .then((json) => {
                console.log(json);
                if (json.ok) setOutfitData(json.outfit);
            });
    }, [outfitId]);

    useEffect(() => {
        fetch("https://wilds.mhdb.io/en/armor")
            .then((response) => {
                return response.json();
            })
            .then((json) => {
                const data = json.map((o: any) => {
                    return {
                        value: o.id,
                        slotType: o.kind,
                        game: "Wilds",
                        dyeable: true,
                        label: o.name,
                    };
                });

                setArmorDb(data);
            });

        fetch("https://wilds.mhdb.io/en/weapons")
            .then((response) => {
                return response.json();
            })
            .then((json) => {
                const data = json.map((o: any) => {
                    return {
                        value: o.id,
                        label: o.name,
                    };
                });

                setWeaponDb(data);
            });
    }, []);

    const hasPermissions = useMemo(() => {
        if (user?.isModerator) return true;

        if (!user || !outfitData) return false;
        return user.id === outfitData.creatorId && outfitData.creatorId;
    }, [user, outfitData]);

    if (!outfitData)
        return (
            <>
                <TitleBar title="Hunter Fashion" />
                <div>Loading...</div>
            </>
        );

    return (
        <>
            <TitleBar title="Hunter Fashion" />
            <div
                className={styles.page}
                style={
                    outfitData
                        ? ({
                              "--accent-a": outfitData?.accentColor,
                              "--accent-a-contrast": getContrastColor(
                                  outfitData?.accentColor,
                              ),
                          } as React.CSSProperties)
                        : {}
                }
            >
                <HSeparator
                    text={outfitData.name}
                    thickness={15}
                    style={{ fontSize: "2rem", padding: "0.5em 1em" }}
                />
                <HSeparator text="Images" />
                {outfitData.layout === "even" && (
                    <ImageLayoutEven images={outfitData?.images} />
                )}
                {outfitData.layout === "wide-4" && (
                    <ImageLayoutWide4 images={outfitData?.images} />
                )}
                {outfitData.layout === "wide-2" && (
                    <ImageLayoutWide2 images={outfitData?.images} />
                )}
                {outfitData.layout === "carousel-left" && (
                    <ImageLayoutCarouselLeft images={outfitData?.images} />
                )}
                {outfitData.layout === "trio" && (
                    <ImageLayoutTrio images={outfitData?.images} />
                )}
                {outfitData.layout === "carousel-center" && (
                    <ImageLayoutCarouselCenter images={outfitData?.images} />
                )}

                <HSeparator text="Tell Us About It" />
                <div className={styles.descriptionText}>
                    {outfitData.description.split(/(\\r)?\\n/g).map((s) => (
                        <div>{s}</div>
                    ))}
                </div>
                <TagList
                    accentColor={outfitData.accentColor}
                    tags={outfitData.tags}
                />
                <HSeparator text="Equipment" />
                <div className={styles.equipmentGrid}>
                    <EquipmentSlot
                        options={weaponDb}
                        backLabel="WEAPON"
                        icon={`${import.meta.env.VITE_SERVER_URL}/images/MonsterHunter/Armor/Great_Sword_Icon_White.webp`}
                        value={outfitData?.equipment.weapon}
                    />
                    <EquipmentSlot
                        options={headOptions}
                        backLabel="HEAD"
                        icon={`${import.meta.env.VITE_SERVER_URL}/images/MonsterHunter/Armor/Helmet_Icon_White.webp`}
                        value={outfitData?.equipment.head.id}
                        bodyType={outfitData?.equipment.head.bodyType}
                        primaryColor={outfitData?.equipment.head.primaryColor}
                        secondaryColor={
                            outfitData?.equipment.head.secondaryColor
                        }
                        canColor
                    />
                    <EquipmentSlot
                        options={chestOptions}
                        backLabel="CHEST"
                        icon={`${import.meta.env.VITE_SERVER_URL}/images/MonsterHunter/Armor/Chest_Icon_White.webp`}
                        value={outfitData?.equipment.chest.id}
                        bodyType={outfitData?.equipment.chest.bodyType}
                        primaryColor={outfitData?.equipment.chest.primaryColor}
                        secondaryColor={
                            outfitData?.equipment.chest.secondaryColor
                        }
                        canColor
                    />
                    <EquipmentSlot
                        options={armsOptions}
                        backLabel="ARMS"
                        icon={`${import.meta.env.VITE_SERVER_URL}/images/MonsterHunter/Armor/Arm_Icon_White.webp`}
                        value={outfitData?.equipment.arms.id}
                        bodyType={outfitData?.equipment.chest.bodyType}
                        primaryColor={outfitData?.equipment.arms.primaryColor}
                        secondaryColor={
                            outfitData?.equipment.arms.secondaryColor
                        }
                        canColor
                    />
                    <EquipmentSlot
                        options={waistOptions}
                        backLabel="WAIST"
                        icon={`${import.meta.env.VITE_SERVER_URL}/images/MonsterHunter/Armor/Waist_Icon_White.webp`}
                        value={outfitData?.equipment.waist?.id}
                        primaryColor={outfitData?.equipment.waist.primaryColor}
                        secondaryColor={
                            outfitData?.equipment.waist.secondaryColor
                        }
                        bodyType={outfitData?.equipment.waist.bodyType}
                        canColor
                    />
                    <EquipmentSlot
                        options={legsOptions}
                        backLabel="LEGS"
                        icon={`${import.meta.env.VITE_SERVER_URL}/images/MonsterHunter/Armor/Leg_Icon_White.webp`}
                        value={outfitData?.equipment.legs?.id}
                        primaryColor={outfitData?.equipment.legs.primaryColor}
                        secondaryColor={
                            outfitData?.equipment.legs.secondaryColor
                        }
                        bodyType={outfitData?.equipment.legs.bodyType}
                        canColor
                    />
                </div>
                {hasPermissions && (
                    <div className={styles.ownerControls}>
                        <button
                            style={{
                                width: "200px",
                                background: "var(--accent-a)",
                                color: getContrastColor(outfitData.accentColor),
                                margin: "0.5em",
                            }}
                            onClick={() => {
                                if (hasPermissions)
                                    navigate(
                                        `/hunterfashion/editor/${outfitId}`,
                                    );
                            }}
                        >
                            Edit
                        </button>
                        <button
                            className="border-button"
                            style={
                                {
                                    width: "200px",
                                    "--accent-d": "#f00",
                                    margin: "0.5em",
                                } as React.CSSProperties
                            }
                            onClick={() => {
                                if (hasPermissions)
                                    showModal(
                                        <ConfirmationModal
                                            title="Delete?"
                                            prompt="Are you sure you want to delete this outfit?\nThis cannot be undone."
                                            onOk={() => {
                                                const url = `${import.meta.env.VITE_SERVER_URL}/api/hunterfashion/${outfitId}/delete`;
                                                fetch(url, {
                                                    credentials: "include",
                                                });
                                                navigate(
                                                    "/hunterfashion/collection",
                                                );
                                                hideModal();
                                            }}
                                            onCancel={() => {
                                                hideModal();
                                            }}
                                        />,
                                    );
                            }}
                        >
                            Delete
                        </button>
                    </div>
                )}
                <div style={{ height: "256px" }}></div>
            </div>
        </>
    );
}
